import { getCartErrorMessage } from '@theme/cart-feedback';

/**
 * <s-shop-the-set> — Two-piece "shop the set" panel on the product page.
 *
 * Both pieces are [data-piece] rows (snippets/shop-the-set-piece.liquid) with
 * their own color and size, so a shopper can buy a top and bottom in different
 * sizes or prints. Resolves each piece's variant and price, and each piece has
 * its own "Add to bag" that adds just that piece.
 *
 * Child markup:
 *   [data-piece][data-role="main"|"mate"][data-lead-id]
 *     [data-piece-header="<productId>"]   — thumbnail/title/price per product; [data-piece-price], [data-piece-compare]
 *     [data-piece-colors] input radios     — value = product id, data-color-name; [data-color-label]
 *     [data-piece-options="<productId>"]  — script[data-piece-variants] and fieldset[data-piece-option][data-option-index]
 *   [data-mate-choice]                     — Optional radios choosing between several matching pieces.
 *   [data-mate-panel][data-mate-index]     — Wraps each matching piece.
 *   [data-piece-add], [data-piece-status] — Per-piece "Add to bag" and its note.
 */
class SShopTheSet extends HTMLElement {
  #ac;
  #busy = false;
  #variantCache = new WeakMap();

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLInputElement)) return;
      // A new choice clears that piece's "Select a size" / "Added" note.
      const changedStatus = target.closest('[data-piece]')?.querySelector('[data-piece-status]');
      if (changedStatus) changedStatus.textContent = '';
      if (target.matches('[data-mate-choice]')) {
        this.#showMate(target.value);
      } else if (target.closest('[data-piece-colors]')) {
        const piece = target.closest('[data-piece]');
        if (piece instanceof HTMLElement) {
          this.#showProduct(piece, target.value);
          if (piece.dataset.role === 'main' && this.dataset.matchColor === 'true') {
            this.#matchMateColor(target.dataset.colorName || '');
          }
        }
      } else if (target.closest('[data-option-color]')) {
        const piece = target.closest('[data-piece]');
        if (piece instanceof HTMLElement && piece.dataset.role === 'main' && this.dataset.matchColor === 'true') {
          this.#matchMateColor(target.value);
        }
      }
      this.#update();
    }, opts);

    // Each piece's own "Add to bag" adds just that piece.
    this.addEventListener('click', (event) => {
      const button = event.target instanceof Element ? event.target.closest('[data-piece-add]') : null;
      const piece = button?.closest('[data-piece]');
      if (button instanceof HTMLElement && piece instanceof HTMLElement) this.#addPiece(piece, button);
    }, opts);

    // Follow the main variant picker: mirror its choice onto "Your piece".
    const scope = this.parentElement;
    scope?.addEventListener('change', (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement) || this.contains(target)) return;
      if (!target.closest('s-variant-picker')) return;
      // The product form partial re-renders after a variant change; read the new id afterwards.
      setTimeout(() => this.#syncFromMainForm(), 400);
      setTimeout(() => this.#syncFromMainForm(), 1200);
    }, opts);

    if (this.dataset.matchColor === 'true') this.#matchMateColor(this.#mainColorName());
    this.#update();
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  /* ---------- structure ---------- */

  #mainPiece() {
    return this.querySelector('[data-piece][data-role="main"]');
  }

  #activeMatePiece() {
    return this.querySelector('[data-mate-panel]:not([hidden]) [data-piece]');
  }

  #showMate(index) {
    this.querySelectorAll('[data-mate-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.mateIndex !== String(index);
    });
  }

  #activeProductId(piece) {
    const checked = piece.querySelector('[data-piece-colors] input[type="radio"]:checked');
    return checked instanceof HTMLInputElement ? checked.value : piece.dataset.leadId || '';
  }

  #optionsBlock(piece, productId = this.#activeProductId(piece)) {
    return piece.querySelector(`[data-piece-options="${productId}"]`);
  }

  #variants(block) {
    if (!block) return [];
    if (this.#variantCache.has(block)) return this.#variantCache.get(block);
    let variants = [];
    try {
      variants = JSON.parse(block.querySelector('[data-piece-variants]')?.textContent || '[]');
    } catch {
      variants = [];
    }
    this.#variantCache.set(block, variants);
    return variants;
  }

  /** Swap a piece to another print, keeping any size already chosen when that size exists. */
  #showProduct(piece, productId) {
    const previous = [...piece.querySelectorAll('[data-piece-options]:not([hidden]) [data-piece-option]')]
      .map((group) => {
        const checked = group.querySelector('input[type="radio"]:checked');
        return checked instanceof HTMLInputElement ? [group.dataset.optionName, checked.value] : null;
      })
      .filter(Boolean);

    piece.querySelectorAll('[data-piece-header]').forEach((el) => {
      el.hidden = el.dataset.pieceHeader !== productId;
    });
    piece.querySelectorAll('[data-piece-options]').forEach((el) => {
      el.hidden = el.dataset.pieceOptions !== productId;
    });

    const block = this.#optionsBlock(piece, productId);
    previous.forEach(([name, value]) => {
      const group = block?.querySelector(`[data-piece-option][data-option-name="${CSS.escape(name)}"]`);
      group?.querySelectorAll('input[type="radio"]').forEach((radio) => {
        if (radio.value === value) radio.checked = true;
      });
    });

    const checked = piece.querySelector('[data-piece-colors] input[type="radio"]:checked');
    const label = piece.querySelector('[data-color-label]');
    if (label && checked instanceof HTMLInputElement) label.textContent = `: ${checked.dataset.colorName || ''}`;
  }

  #matchMateColor(colorName) {
    if (!colorName) return;
    this.querySelectorAll('[data-piece][data-role="mate"]').forEach((piece) => {
      // Colors sold as separate products.
      const match = [...piece.querySelectorAll('[data-piece-colors] input[type="radio"]')]
        .find((radio) => radio instanceof HTMLInputElement && radio.dataset.colorName === colorName);
      if (match instanceof HTMLInputElement && !match.checked) {
        match.checked = true;
        this.#showProduct(piece, match.value);
      }
      // Colors sold as options of one product.
      const option = [...(this.#optionsBlock(piece)?.querySelectorAll('[data-option-color] input[type="radio"]') || [])]
        .find((radio) => radio instanceof HTMLInputElement && radio.value.toLowerCase() === colorName.toLowerCase());
      if (option instanceof HTMLInputElement && !option.checked) option.checked = true;
    });
  }

  /** The main piece's current color name, from either kind of swatch. */
  #mainColorName() {
    const piece = this.#mainPiece();
    if (!(piece instanceof HTMLElement)) return '';
    const option = this.#optionsBlock(piece)?.querySelector('[data-option-color] input[type="radio"]:checked');
    if (option instanceof HTMLInputElement) return option.value;
    const product = piece.querySelector('[data-piece-colors] input[type="radio"]:checked');
    return product instanceof HTMLInputElement ? product.dataset.colorName || '' : '';
  }

  #syncFromMainForm() {
    const piece = this.#mainPiece();
    if (!(piece instanceof HTMLElement)) return;
    if (this.#activeProductId(piece) !== piece.dataset.leadId) return;
    const input = this.parentElement?.querySelector('s-product-form input[name="id"]');
    if (!(input instanceof HTMLInputElement)) return;
    const block = this.#optionsBlock(piece);
    const variant = this.#variants(block).find((v) => String(v.id) === input.value);
    if (!variant || !block) return;
    block.querySelectorAll('[data-piece-option]').forEach((group) => {
      const value = variant.options[Number(group.dataset.optionIndex)];
      group.querySelectorAll('input[type="radio"]').forEach((radio) => {
        if (radio.value === value) radio.checked = true;
      });
    });
    this.#update();
  }

  /* ---------- selection ---------- */

  #selection(piece) {
    if (!(piece instanceof HTMLElement)) return { complete: false, variant: null, estimate: 0 };
    const productId = this.#activeProductId(piece);
    const block = this.#optionsBlock(piece, productId);
    const variants = this.#variants(block);
    const optionCount = variants[0]?.options.length || 0;
    const selected = new Array(optionCount).fill(null);
    const groups = block ? [...block.querySelectorAll('[data-piece-option]')] : [];

    groups.forEach((group) => {
      const index = Number(group.dataset.optionIndex);
      const checked = group.querySelector('input[type="radio"]:checked');
      selected[index] = checked instanceof HTMLInputElement ? checked.value : null;
      const label = group.querySelector('[data-selected-label]');
      if (label) label.textContent = selected[index] ? `: ${selected[index]}` : '';
    });

    // Options without buttons have a single value, so they match any variant.
    const grouped = new Set(groups.map((group) => Number(group.dataset.optionIndex)));
    const matches = (variant, skip = -1) => variant.options.every((value, i) => i === skip
      || !grouped.has(i) || selected[i] === null || selected[i] === value);

    // Fade and strike values that can't be bought with the other current choices.
    groups.forEach((group) => {
      const index = Number(group.dataset.optionIndex);
      group.querySelectorAll('input[type="radio"]').forEach((radio) => {
        const possible = variants.some((variant) => variant.available
          && variant.options[index] === radio.value && matches(variant, index));
        const label = radio.closest('label');
        label?.classList.toggle('opacity-40', !possible);
        label?.classList.toggle('line-through', !possible);
      });
    });

    const complete = groups.every((group) => selected[Number(group.dataset.optionIndex)] !== null);
    const candidates = variants.filter((variant) => matches(variant));
    const variant = complete ? candidates[0] || null : null;
    const shown = variant || candidates.find((v) => v.available) || candidates[0] || null;
    this.#renderPrice(piece, productId, shown);
    const estimate = candidates.length ? Math.min(...candidates.map((v) => v.price)) : 0;
    return { complete, variant, estimate };
  }

  #renderPrice(piece, productId, variant) {
    if (!variant) return;
    const header = piece.querySelector(`[data-piece-header="${productId}"]`);
    const price = header?.querySelector('[data-piece-price]');
    const compare = header?.querySelector('[data-piece-compare]');
    const onSale = variant.compare > variant.price;
    if (price) {
      price.textContent = this.#format(variant.price);
      price.classList.toggle('text-sale', onSale);
      price.classList.toggle('text-on-surface', !onSale);
    }
    if (compare instanceof HTMLElement) {
      compare.hidden = !onSale;
      compare.textContent = onSale ? this.#format(variant.compare) : '';
    }
    // Show the chosen color's photo, and point the links at that color.
    const image = header?.querySelector('[data-piece-image]');
    if (image instanceof HTMLImageElement && variant.image && image.getAttribute('src') !== variant.image) {
      image.src = variant.image;
      if (variant.srcset) image.srcset = variant.srcset;
    }
    if (variant.url) {
      header?.querySelectorAll('a[href]').forEach((link) => link.setAttribute('href', variant.url));
    }
  }

  #format(cents) {
    try {
      return new Intl.NumberFormat(this.dataset.locale || undefined, {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  /** Refresh each piece's price for its current choices. */
  #update() {
    this.querySelectorAll('[data-piece]').forEach((piece) => this.#selection(piece));
  }

  /* ---------- cart ---------- */

  /** A piece's own "Add to bag": just that piece's chosen variant. */
  async #addPiece(piece, button) {
    if (this.#busy) return;
    const status = piece.querySelector('[data-piece-status]');
    const selection = this.#selection(piece);
    const show = (message, isError = false) => {
      if (!status) return;
      status.textContent = message || '';
      status.classList.toggle('text-sale', isError);
    };
    if (!selection.complete) {
      show(this.dataset.selectSizeMessage);
      return;
    }
    if (!selection.variant?.available) {
      show(this.dataset.unavailableMessage);
      return;
    }
    const added = await this.#addLines([selection.variant.id], button, status);
    if (added) {
      show(this.dataset.addedMessage);
      setTimeout(() => {
        if (status?.textContent === this.dataset.addedMessage) show('');
      }, 4000);
    }
  }

  async #addLines(variantIds, button, status) {
    let errorMessage = '';
    const lines = variantIds.map((id) => ({ merchandiseId: String(id), quantity: 1 }));

    this.#busy = true;
    this.setAttribute('aria-busy', 'true');
    button?.classList.add('cursor-wait', 'opacity-50');

    try {
      if (window.Shopify?.actions?.updateCart) {
        const result = await window.Shopify.actions.updateCart(
          { lines },
          { event: { context: 'product', detail: { source: 's-shop-the-set' } } }
        );
        const error = result?.userErrors?.[0]?.message;
        if (error) throw new Error(error);
        await window.Shopify.actions.openCart?.();
      } else {
        const response = await fetch(`${this.dataset.cartAddUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: lines.map((line) => ({ id: Number(line.merchandiseId), quantity: 1 })) }),
        });
        if (!response.ok) throw new Error((await response.json())?.description || '');
        window.location.href = this.dataset.cartUrl;
      }
    } catch (error) {
      errorMessage = getCartErrorMessage(error) || error?.message || this.dataset.errorMessage || '';
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
      button?.classList.remove('cursor-wait', 'opacity-50');
      this.#update();
      if (errorMessage && status) {
        status.textContent = errorMessage;
        status.classList.add('text-sale');
      }
    }
    return !errorMessage;
  }
}

if (!customElements.get('s-shop-the-set')) {
  customElements.define('s-shop-the-set', SShopTheSet);
}
